import React, { useState, useEffect } from 'react';
import { Document as DocType } from '../services/documents.service';
import { propertiesService, Property } from '../services/properties.service';
import { expensesService, CreateExpenseData } from '../services/expenses.service';
import { tagsService, Tag } from '../services/tags.service';
import { categoriesService, Category } from '../services/categories.service';
import { aiRulesService } from '../services/aiRules.service';
import { api } from '../config/api';
import { FiX, FiFileText, FiImage, FiMail, FiCalendar, FiMapPin, FiDollarSign, FiChevronLeft, FiChevronRight, FiSave, FiCpu, FiZap, FiCheckCircle, FiRefreshCw } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { TagSelector } from './TagSelector';

interface DocumentViewerProps {
  document: DocType;
  onClose: () => void;
  onCreateExpense?: (data: any) => void;
  onDocumentUpdate?: () => void;
  isFromQueue?: boolean;
  isEmbedded?: boolean;
}

interface AISuggestions {
  filename?: string;
  vendor?: string;
  amount?: number;
  date?: string;
  description?: string;
  documentType?: string;
  propertyAddress?: string;
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({ document, onClose, onCreateExpense, onDocumentUpdate, isFromQueue = false, isEmbedded = false }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [ocrText, setOcrText] = useState<string>('');
  const [documentUrl, setDocumentUrl] = useState<string>('');
  const [fileHtmlContent, setFileHtmlContent] = useState<string>('');
  const [suggestions, setSuggestions] = useState<AISuggestions>({});
  const [activeTab, setActiveTab] = useState<'preview' | 'ocr'>('preview');
  const [isProcessing, setIsProcessing] = useState(false);
  const [removeFromQueue, setRemoveFromQueue] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  
  // Debug log to verify prop is being passed
  console.log('DocumentViewer - isFromQueue:', isFromQueue, 'isEmbedded:', isEmbedded, 'document:', document.filename);
  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(document.property_id || null);
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>('');
  const [hasExtractedData, setHasExtractedData] = useState<boolean | null>(null);
  const [isCreatingExpense, setIsCreatingExpense] = useState(false);
  const [isFilingDocument, setIsFilingDocument] = useState(false);
  const [documentTags, setDocumentTags] = useState<Tag[]>([]);
  // Individual state for each field to ensure they're editable
  const [editedFilename, setEditedFilename] = useState<string>('');
  const [editedVendor, setEditedVendor] = useState<string>('');
  const [editedAmount, setEditedAmount] = useState<string>('');
  const [editedDate, setEditedDate] = useState<string>('');
  const [editedDescription, setEditedDescription] = useState<string>('');
  
  // Document types
  const documentTypes = [
    'Invoice', 
    'Business Expense',
    'Receipt', 
    'Mortgage Statement',
    'Utility Bill',
    'HOA Notice', 
    'Insurance Document',
    'Tax Document',
    'Contract/Lease', 
    'Repair/Maintenance',
    'Proposal',
    'Quote',
    'Credit Card Statement',
    'Other'
  ];

  // Map display names to backend enum values
  const documentTypeMap: { [key: string]: string } = {
    'Invoice': 'expense',
    'Business Expense': 'business_expense',
    'Receipt': 'expense',
    'Mortgage Statement': 'mortgage_statement',
    'Utility Bill': 'utility_bill',
    'HOA Notice': 'hoa_document',
    'Insurance Document': 'insurance_document',
    'Tax Document': 'tax_document',
    'Contract/Lease': 'contract',
    'Repair/Maintenance': 'repair_maintenance',
    'Proposal': 'proposal',
    'Quote': 'quote',
    'Credit Card Statement': 'credit_card_statement',
    'Other': 'other'
  };

  useEffect(() => {
    loadDocumentData();
    loadProperties();
    loadCategories();
    // Initialize selectedPropertyId with document's property_id
    setSelectedPropertyId(document.property_id || null);
    // Cleanup blob URL on unmount or document change
    return () => {
      if (documentUrl && documentUrl.startsWith('blob:')) {
        window.URL.revokeObjectURL(documentUrl);
      }
    };
  }, [document.id]);

  // State to track if fields have been initialized
  const [fieldsInitialized, setFieldsInitialized] = useState(false);

  // Initialize fields when suggestions change (only once)
  useEffect(() => {
    if (!fieldsInitialized && Object.keys(suggestions).length > 0) {
      console.log('Initializing fields with suggestions:', suggestions);
      setEditedFilename(suggestions.filename || '');
      setEditedVendor(suggestions.vendor || '');
      setEditedAmount(suggestions.amount ? suggestions.amount.toString() : '');
      setEditedDate(suggestions.date || '');
      setEditedDescription(suggestions.description || '');
      setFieldsInitialized(true);
    }
  }, [suggestions, fieldsInitialized]);

  const loadProperties = async () => {
    try {
      const data = await propertiesService.getProperties();
      setProperties(data);
    } catch (error) {
      console.error('Failed to load properties:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await categoriesService.getExpenseCategories();
      setCategories(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadDocumentData = async () => {
    try {
      setIsLoading(true);
      
      // Debug auth
      const token = localStorage.getItem('accessToken');
      console.log('Auth token present:', !!token);
      console.log('Token preview:', token ? token.substring(0, 20) + '...' : 'No token');
      
      // Get full document data including AI predictions
      let fullDocument = document; // Use passed document as fallback
      try {
        const docResponse = await api.get(`/documents/${document.id}`);
        fullDocument = docResponse.data;
      } catch (error: any) {
        console.error('Failed to load full document, using partial data:', error);
        // Continue with the document data we already have
      }
      
      // Set the property from the document if it has one
      // If no property is set, it will remain null (General/All Properties)
      if (fullDocument.property_id) {
        setSelectedPropertyId(fullDocument.property_id);
      } else {
        setSelectedPropertyId(null); // Explicitly set to null for General/All Properties
      }
      
      // Load OCR text if available
      if (fullDocument.ocr_text) {
        setOcrText(fullDocument.ocr_text);
      }
      
      // Load tags if available
      if (fullDocument.tags) {
        setDocumentTags(fullDocument.tags);
      }
      
      // Load AI extracted data if available
      if (fullDocument.extracted_data) {
        console.log('AI extracted data:', fullDocument.extracted_data);
        setHasExtractedData(true);
        const extractedSuggestions = {
          filename: fullDocument.extracted_data.filename,
          vendor: fullDocument.extracted_data.vendor,
          amount: fullDocument.extracted_data.amount,
          date: fullDocument.extracted_data.date,
          description: fullDocument.extracted_data.description,
          documentType: fullDocument.extracted_data.type,
          propertyAddress: fullDocument.extracted_data.property_address
        };
        setSuggestions(extractedSuggestions);
        // Set document type if available
        if (fullDocument.extracted_data.type) {
          setSelectedDocumentType(fullDocument.extracted_data.type || 'Other');
        }
      } else {
        // Fall back to text extraction if no AI predictions
        setHasExtractedData(false);
        const textToAnalyze = fullDocument.ocr_text || document.description || '';
        extractSuggestions(textToAnalyze);
      }
      
      // Also get suggested copy destination from document
      if (fullDocument?.suggested_copy_destination_name) {
        console.log('Suggested copy destination:', fullDocument.suggested_copy_destination_name);
      }

      // Load document preview URL for non-email documents
      if (document.file_path !== 'email_content') {
        try {
          // For debugging
          console.log('Loading document:', document);
          console.log('Document mime_type:', document.mime_type);
          console.log('Document ID:', document.id);
          console.log('Auth token:', localStorage.getItem('accessToken') ? 'Present' : 'Missing');
          
          const response = await api.get(`/documents/${document.id}/download`, {
            responseType: 'blob'
          });
          
          console.log('Response received, size:', response.data.size);
          console.log('Response type:', response.data.type);
          
          // Determine mime type from filename if not provided
          let mimeType = document.mime_type || response.data.type;
          if (!mimeType || mimeType === 'application/octet-stream') {
            const ext = document.filename.split('.').pop()?.toLowerCase();
            if (ext === 'pdf') mimeType = 'application/pdf';
            else if (['jpg', 'jpeg'].includes(ext || '')) mimeType = 'image/jpeg';
            else if (ext === 'png') mimeType = 'image/png';
          }
          
          const blob = new Blob([response.data], { type: mimeType });
          const url = window.URL.createObjectURL(blob);
          setDocumentUrl(url);
          
          console.log('Blob URL created:', url);
          console.log('Final mime type:', mimeType);
          
          // For .txt files, read content as text
          const ext = document.filename.split('.').pop()?.toLowerCase();
          if (ext === 'txt' || mimeType === 'text/plain' || mimeType === 'application/octet-stream') {
            try {
              const textBlob = new Blob([response.data], { type: 'text/plain' });
              const textContent = await textBlob.text();
              // Check if it contains actual HTML
              if (textContent && (
                /<html[\s>]/i.test(textContent) ||
                /<body[\s>]/i.test(textContent) ||
                (/<table[\s>]/i.test(textContent) && /<\/table>/i.test(textContent))
              )) {
                console.log('Detected HTML content in .txt file');
                setFileHtmlContent(textContent);
              } else if (textContent) {
                // For email .txt files, convert to clean HTML
                const isEmailText = /^-{5,}\s*Forwarded message/m.test(textContent) ||
                  (/^From:\s/m.test(textContent) && /\[image:/i.test(textContent));
                if (isEmailText) {
                  console.log('Detected Gmail plain text email, converting to clean HTML');
                  const cleanHtml = convertEmailTextToHtml(textContent);
                  setFileHtmlContent(cleanHtml);
                }
                // Also store raw text for OCR tab
                if (!ocrText) {
                  setOcrText(textContent);
                }
              }
            } catch (textErr) {
              console.error('Error reading .txt file as text:', textErr);
            }
          }
        } catch (error: any) {
          console.error('Error loading document preview:', error);
          console.error('Error response:', error.response);
          if (error.response?.status === 401) {
            toast.error('Authentication failed. Please log in again.');
          } else if (error.response?.status === 404) {
            toast.error('Document file not found');
          } else {
            toast.error(`Failed to load document: ${error.response?.data?.detail || error.message}`);
          }
        }
      }
      
    } catch (error) {
      console.error('Error loading document data:', error);
      toast.error('Failed to load document data');
    } finally {
      setIsLoading(false);
    }
  };

  const extractSuggestions = (text: string) => {
    const suggestions: AISuggestions = {};
    
    // Log for debugging
    console.log('Extracting suggestions from text:', text?.substring(0, 500) || 'No text available');

    if (!text || text.length === 0) {
      console.log('No text available for extraction');
      return suggestions;
    }

    // Extract vendor/company name (look for common patterns)
    const vendorPatterns = [
      /^([A-Z][A-Za-z\s&,.\-]+)(?:\n|$)/m,  // First line that starts with capital
      /From:\s*([A-Za-z\s&,.\-]+)/i,
      /Company:\s*([A-Za-z\s&,.\-]+)/i,
      /Vendor:\s*([A-Za-z\s&,.\-]+)/i,
      /Bill\s+To:?\s*([A-Za-z\s&,.\-]+)/i,
      /Invoice\s+To:?\s*([A-Za-z\s&,.\-]+)/i,
    ];
    
    for (const pattern of vendorPatterns) {
      const match = text.match(pattern);
      if (match && match[1]) {
        suggestions.vendor = match[1].trim().substring(0, 50); // Limit length
        console.log('Found vendor:', suggestions.vendor);
        break;
      }
    }

    // Extract amount
    const amountPatterns = [
      /Total[:\s]*\$?\s*([0-9,]+\.?\d*)/i,
      /Amount[:\s]*\$?\s*([0-9,]+\.?\d*)/i,
      /Balance[:\s]*\$?\s*([0-9,]+\.?\d*)/i,
      /Grand\s+Total[:\s]*\$?\s*([0-9,]+\.?\d*)/i,
      /Amount\s+Due[:\s]*\$?\s*([0-9,]+\.?\d*)/i,
      /\$\s*([0-9,]+\.?\d*)/,
    ];

    for (const pattern of amountPatterns) {
      const match = text.match(pattern);
      if (match && match[1]) {
        const amount = parseFloat(match[1].replace(/,/g, ''));
        if (!isNaN(amount) && amount > 0) {
          suggestions.amount = amount;
          console.log('Found amount:', suggestions.amount);
          break;
        }
      }
    }

    // Extract date
    const datePatterns = [
      /Date[:\s]*(\d{1,2}[-/]\d{1,2}[-/]\d{2,4})/i,
      /Invoice Date[:\s]*(\d{1,2}[-/]\d{1,2}[-/]\d{2,4})/i,
      /(\d{1,2}[-/]\d{1,2}[-/]\d{2,4})/,
    ];

    for (const pattern of datePatterns) {
      const match = text.match(pattern);
      if (match) {
        suggestions.date = match[1];
        break;
      }
    }

    // Extract property address
    const addressPatterns = [
      /Property[:\s]*([0-9]+\s+[A-Za-z\s]+(?:Street|St|Avenue|Ave|Road|Rd|Drive|Dr|Lane|Ln|Boulevard|Blvd)[,\s]+[A-Za-z\s]+,?\s*[A-Z]{2}\s*\d{5})/i,
      /Address[:\s]*([0-9]+\s+[A-Za-z\s]+)/i,
      /([0-9]+\s+[A-Za-z\s]+(?:Street|St|Avenue|Ave|Road|Rd|Drive|Dr|Lane|Ln|Boulevard|Blvd))/i,
    ];

    for (const pattern of addressPatterns) {
      const match = text.match(pattern);
      if (match) {
        suggestions.propertyAddress = match[1].trim();
        break;
      }
    }

    // Determine document type
    const docTypeKeywords = {
      invoice: /invoice|bill|statement/i,
      receipt: /receipt|paid|payment/i,
      hoa: /hoa|homeowner|association/i,
      contract: /contract|agreement/i,
      proposal: /proposal|quote|estimate/i,
      car_repair: /auto|car|vehicle|repair|mechanic|automotive/i,
      dining: /restaurant|cafe|food|dining|meal/i,
      clothing: /clothing|apparel|wear|fashion/i,
      office_supplies: /office|supplies|stationery|printer/i,
      travel: /hotel|flight|travel|airline|lodging/i,
      utilities: /electric|gas|water|utility|utilities/i,
      insurance: /insurance|policy|premium|coverage/i,
      tax: /tax|irs|1099|w2|return/i,
    };

    for (const [type, pattern] of Object.entries(docTypeKeywords)) {
      if (pattern.test(text)) {
        suggestions.documentType = type;
        break;
      }
    }

    // Generate filename suggestion
    if (suggestions.vendor && suggestions.date) {
      const cleanVendor = suggestions.vendor.replace(/[^a-zA-Z0-9]/g, '_');
      const cleanDate = suggestions.date.replace(/[/-]/g, '_');
      suggestions.filename = `${cleanVendor}_${cleanDate}.pdf`;
    }

    setSuggestions(suggestions);
  };

  const handleProcessDocument = async () => {
    setIsProcessing(true);
    try {
      const response = await api.post(`/documents/${document.id}/process`);
      if (response.status === 200) {
        toast.success('Document processing started');
        // Reload document data after a short delay
        setTimeout(() => {
          loadDocumentData();
        }, 2000);
      }
    } catch (error) {
      console.error('Error processing document:', error);
      toast.error('Failed to process document');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRetryAI = async () => {
    setIsProcessing(true);
    try {
      toast.loading('Retrying AI extraction...', { id: 'retry-ai' });
      
      // Use the regular process endpoint that actually performs AI extraction
      const endpoint = `/documents/${document.id}/process`;
      
      const response = await api.post(endpoint);
      
      if (response.status === 200) {
        // Check if processing was successful
        if (response.data.success) {
          toast.success('Document processed successfully!', { id: 'retry-ai' });
          
          // Reload document data to get full update
          setTimeout(() => {
            loadDocumentData();
          }, 1000);
        } else {
          // Processing failed but we got a response
          const errorMsg = response.data.error || response.data.message || 'Processing failed';
          toast.error(errorMsg, { id: 'retry-ai' });
          
          if (response.data.suggestion) {
            toast(response.data.suggestion, { 
              duration: 5000,
              icon: 'ℹ️'
            });
          }
        }
      }
    } catch (error: any) {
      console.error('Error retrying AI:', error);
      // Log more details to help debug
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        console.error('Response headers:', error.response.headers);
      }
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to retry AI extraction';
      toast.error(errorMessage, { id: 'retry-ai' });
      
      // If regular retry failed, offer force complete option
      toast(
        <div>
          <p>AI processing failed. Would you like to mark as completed anyway?</p>
          <button
            onClick={async () => {
              try {
                const forceResponse = await api.post(`/stable-docs/force-complete/${document.id}`);
                if (forceResponse.data.success) {
                  toast.success('Document marked as completed');
                  setTimeout(() => loadDocumentData(), 1000);
                }
              } catch (err) {
                toast.error('Failed to force complete document');
              }
            }}
            className="mt-2 px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
          >
            Force Complete
          </button>
        </div>,
        { 
          duration: 10000,
          id: 'force-complete-option'
        }
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateExpense = async () => {
    console.log('handleCreateExpense called in DocumentViewer');
    
    try {
      // Wrap everything in try-catch to prevent white screen
      
      // Get form values using getElementById which is more reliable
      const filenameInput = window.document.getElementById('expense-filename') as HTMLInputElement;
      const vendorInput = window.document.getElementById('expense-vendor') as HTMLInputElement;
      const amountInput = window.document.getElementById('expense-amount') as HTMLInputElement;
      const dateInput = window.document.getElementById('expense-date') as HTMLInputElement;
      const descInput = window.document.getElementById('expense-description') as HTMLTextAreaElement;
      
      console.log('Form inputs found:', {
        filenameInput: !!filenameInput,
        vendorInput: !!vendorInput,
        amountInput: !!amountInput,
        dateInput: !!dateInput,
        descInput: !!descInput
      });
      
      const vendor = vendorInput?.value || suggestions.vendor || '';
      const amount = parseFloat(amountInput?.value || '0') || suggestions.amount || 0;
      const date = dateInput?.value || suggestions.date || 
        (document.document_date ? new Date(document.document_date).toISOString().split('T')[0] : null) ||
        (document.extracted_data?.date || new Date().toISOString().split('T')[0]);
      const description = descInput?.value || suggestions.description || document.email_subject || document.filename;
      
      console.log('Extracted values:', { vendor, amount, date, description });
      
      // Validate required fields
      const numericAmount = parseFloat(amount.toString());
      if (!amount || amount <= 0 || isNaN(numericAmount)) {
        toast.error('Please enter a valid amount');
        console.error('Invalid amount:', { original: amount, parsed: numericAmount });
        return;
      }
      
      if (!date) {
        toast.error('Please enter a date');
        return;
      }
      
      console.log('Creating expense:', { vendor, amount, date, description, propertyId: selectedPropertyId });
      
      setIsCreatingExpense(true);
      
      try {
        // Ensure document ID is a valid number
        const documentIdNum = parseInt(document.id);
        if (isNaN(documentIdNum)) {
          console.error('Invalid document ID:', document.id);
          throw new Error('Invalid document ID');
        }
        
        // Create the expense data
        // Format data exactly like the working ExpenseForm
        const finalPropertyId = selectedPropertyId || document.property_id;
        const expenseData: CreateExpenseData = {
          amount: numericAmount,
          expense_date: date ? new Date(date).toISOString() : new Date().toISOString(),
          property_id: finalPropertyId ? parseInt(finalPropertyId.toString()) : undefined,
          document_id: documentIdNum,
          vendor: vendor || 'Unknown Vendor',
          description: description,
          category_id: selectedCategoryId ? parseInt(selectedCategoryId.toString()) : undefined
        };
        
        console.log('Sending expense data:', expenseData);
        console.log('Document ID type:', typeof document.id, 'value:', document.id, 'converted:', documentIdNum);
        console.log('Selected property ID:', selectedPropertyId, 'type:', typeof selectedPropertyId);
        console.log('Original document property_id:', document.property_id);
        console.log('API endpoint will be called:', '/expenses/');
        
        // Call the expense service to create the expense
        console.log('About to call expensesService.createExpense...');
        const createdExpense = await expensesService.createExpense(expenseData);
        console.log('expensesService.createExpense returned:', createdExpense);
        
        console.log('Expense created successfully:', createdExpense);
        toast.success('Expense created successfully!');
        
        // Update document to match the expense's property and mark as completed
        try {
          const finalPropertyId = selectedPropertyId || document.property_id || null;
          const updateData: any = { 
            processing_status: 'completed',
            property_id: finalPropertyId
          };
          
          console.log(`Updating document: status=completed, property_id from ${document.property_id} to ${finalPropertyId}`);
          
          await api.patch(`/documents/${document.id}`, updateData);
          console.log('Document updated with:', updateData);
        } catch (patchError) {
          console.error('Failed to update document:', patchError);
          // Continue anyway - expense was created successfully
        }
        
        // Call the optional callback if provided
        if (onCreateExpense) {
          console.log('Calling onCreateExpense callback');
          try {
            onCreateExpense({ success: true });
          } catch (callbackError) {
            console.error('Error in onCreateExpense callback:', callbackError);
            // Don't let callback errors break the flow
          }
        }
        
        // Close the modal after a short delay
        setTimeout(() => {
          console.log('Closing modal');
          try {
            onClose();
          } catch (closeError) {
            console.error('Error closing modal:', closeError);
            // Force reload if close fails
            window.location.reload();
          }
        }, 1500);
        
      } catch (error: any) {
        console.error('Error creating expense:', error);
        console.error('Error response:', error.response);
        console.error('Error stack:', error.stack);
        
        let errorMessage = 'Failed to create expense';
        
        // Log the full error for debugging
        console.error('Full error object:', error);
        console.error('Error response data:', error.response?.data);
        
        try {
          if (error.response?.data?.detail) {
            // FastAPI standard error format
            errorMessage = error.response.data.detail;
          } else if (error.response?.data?.message) {
            // Alternative message format
            errorMessage = error.response.data.message;
          } else if (typeof error.response?.data === 'string') {
            // Plain string error
            errorMessage = error.response.data;
          } else if (error.response?.data && typeof error.response.data === 'object') {
            // Handle validation errors or other object responses
            if (Array.isArray(error.response.data)) {
              // Handle array of errors (FastAPI validation errors)
              const firstError = error.response.data[0];
              if (firstError?.msg) {
                errorMessage = firstError.msg;
              } else if (firstError?.message) {
                errorMessage = firstError.message;
              } else {
                errorMessage = 'Validation error';
              }
            } else if (error.response.data.msg) {
              // Single validation error
              errorMessage = error.response.data.msg;
            } else if (error.response.data.error) {
              // Generic error field
              errorMessage = error.response.data.error;
            } else {
              // Try to find any string value in the error object
              const errorValues = Object.values(error.response.data);
              const stringValue = errorValues.find(v => typeof v === 'string');
              if (stringValue) {
                errorMessage = String(stringValue);
              } else {
                // Last resort - show status code
                errorMessage = `Request failed with status ${error.response?.status || 'unknown'}`;
              }
            }
          } else if (error.message) {
            errorMessage = error.message;
          }
        } catch (parseError) {
          console.error('Error parsing error message:', parseError);
          errorMessage = 'An unexpected error occurred';
        }
        
        // Check for specific database errors
        if (errorMessage.includes('duplicate key value violates unique constraint "expenses_document_id_key"')) {
          errorMessage = 'An expense has already been created for this document. Each document can only have one expense.';
        }
        
        // Absolutely ensure errorMessage is a string and not [object Object]
        const finalMessage = String(errorMessage);
        if (finalMessage === '[object Object]') {
          console.error('Error message was [object Object], using fallback');
          toast.error('Failed to create expense. Check console for details.');
        } else {
          toast.error(finalMessage);
        }
      } finally {
        setIsCreatingExpense(false);
      }
    } catch (outerError: any) {
      // Catch any error in the entire function to prevent white screen
      console.error('CRITICAL ERROR in handleCreateExpense:', outerError);
      console.error('Error stack:', outerError.stack);
      toast.error('An unexpected error occurred. Please try again.');
      setIsCreatingExpense(false);
    }
  };

  const handleSaveChanges = async () => {
    console.log('Saving expense field changes');
    
    try {
      // SIMPLIFIED VERSION - Only save extracted_data
      const updateData: any = {};
      
      // Only add extracted_data if we have expense fields to save
      const vendorInput = window.document.getElementById('expense-vendor') as HTMLInputElement;
      const amountInput = window.document.getElementById('expense-amount') as HTMLInputElement;
      const dateInput = window.document.getElementById('expense-date') as HTMLInputElement;
      const descInput = window.document.getElementById('expense-description') as HTMLTextAreaElement;
      
      // Only create extracted_data if at least one field has a value
      if (vendorInput?.value || amountInput?.value || dateInput?.value || descInput?.value) {
        const extractedData: any = {};
        
        if (vendorInput?.value) extractedData.vendor = vendorInput.value;
        if (amountInput?.value) {
          const amount = parseFloat(amountInput.value);
          if (!isNaN(amount)) extractedData.amount = amount;
        }
        if (dateInput?.value) extractedData.date = dateInput.value;
        if (descInput?.value) extractedData.description = descInput.value;
        
        updateData.extracted_data = extractedData;
      }
      
      // If removing from queue, assign to a property and mark as completed
      if (removeFromQueue && isFromQueue) {
        // Always update property_id when removing from queue
        updateData.property_id = selectedPropertyId !== null ? selectedPropertyId : (document.property_id || null);
        // Mark as completed to remove from queue
        updateData.processing_status = 'completed';
        console.log(`Removing from queue: property_id=${updateData.property_id}, status=completed`);
      }
      
      // Add document type if selected
      if (selectedDocumentType) {
        // Check if it's already a backend value by seeing if it contains underscores or is all lowercase
        const isBackendValue = selectedDocumentType.includes('_') || selectedDocumentType === selectedDocumentType.toLowerCase();
        updateData.document_type = isBackendValue 
          ? selectedDocumentType.toLowerCase()  // Ensure it's lowercase
          : (documentTypeMap[selectedDocumentType] || 'other');
        console.log(`Setting document type: ${selectedDocumentType} -> ${updateData.document_type}`);
      }
      
      console.log('Simplified update data:', updateData);
      
      // If there's nothing to update, don't make the request
      if (Object.keys(updateData).length === 0) {
        toast('No changes to save');
        return;
      }
      
      const response = await api.patch(`/documents/${document.id}`, updateData);
      console.log('Save response:', response);
      
      if (removeFromQueue && isFromQueue) {
        toast.success('Changes saved and removed from queue!');
        // Close the modal after removing from queue
        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        toast.success('Changes saved successfully!');
      }
      
      // Reload to get fresh data
      loadDocumentData();
      
    } catch (error: any) {
      console.error('Error saving changes:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save changes';
      toast.error(errorMessage);
    }
  };

  const handleFileDocument = async () => {
    console.log('handleFileDocument called in DocumentViewer');
    
    // Get form values
    const filenameInput = window.document.getElementById('expense-filename') as HTMLInputElement;
    const descInput = window.document.getElementById('expense-description') as HTMLTextAreaElement;
    
    const filename = filenameInput?.value || suggestions.filename || document.filename;
    const description = descInput?.value || suggestions.description || document.email_subject || document.filename;
    
    console.log('Filing document:', { 
      documentId: document.id,
      filename, 
      propertyId: selectedPropertyId, 
      documentType: selectedDocumentType,
      currentPropertyId: document.property_id 
    });
    
    setIsFilingDocument(true);
    
    try {
      // Update document with property and type information
      const updateData: any = {
        processing_status: 'completed'
      };
      
      // Handle document type - check if it's already a backend value
      if (selectedDocumentType) {
        const isBackendValue = selectedDocumentType.includes('_') || selectedDocumentType === selectedDocumentType.toLowerCase();
        updateData.document_type = isBackendValue 
          ? selectedDocumentType.toLowerCase()  // Ensure it's lowercase
          : (documentTypeMap[selectedDocumentType] || 'other');
      }
      
      // Add property_id - it can be null (General) or a number
      // We should always include it to ensure proper filing
      updateData.property_id = selectedPropertyId;
      
      // If filename changed, update it
      if (filename !== document.filename) {
        updateData.suggested_copy_destination_name = filename;
      }
      
      console.log('Updating document with:', updateData);
      
      const response = await api.patch(`/documents/${document.id}`, updateData);
      console.log('Document update response:', response.data);
      
      toast.success('Document filed successfully!');
      
      // Trigger a refresh of the parent component if callback provided
      if (onDocumentUpdate) {
        onDocumentUpdate();
      }
      
      // Close the modal after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error: any) {
      console.error('Error filing document:', error);
      console.error('Error response:', error.response);
      
      let errorMessage = 'Failed to file document';
      
      // Log the full error for debugging
      console.error('Full error object:', error);
      console.error('Error response data:', error.response?.data);
      
      try {
        if (error.response?.data?.detail) {
          // FastAPI standard error format
          errorMessage = error.response.data.detail;
        } else if (error.response?.data?.message) {
          // Alternative message format
          errorMessage = error.response.data.message;
        } else if (typeof error.response?.data === 'string') {
          // Plain string error
          errorMessage = error.response.data;
        } else if (error.response?.data && typeof error.response.data === 'object') {
          // Handle validation errors or other object responses
          if (Array.isArray(error.response.data)) {
            // Handle array of errors (FastAPI validation errors)
            const firstError = error.response.data[0];
            if (firstError?.msg) {
              errorMessage = firstError.msg;
            } else if (firstError?.message) {
              errorMessage = firstError.message;
            } else {
              errorMessage = 'Validation error';
            }
          } else if (error.response.data.msg) {
            // Single validation error
            errorMessage = error.response.data.msg;
          } else if (error.response.data.error) {
            // Generic error field
            errorMessage = error.response.data.error;
          } else {
            // Try to find any string value in the error object
            const errorValues = Object.values(error.response.data);
            const stringValue = errorValues.find(v => typeof v === 'string');
            if (stringValue) {
              errorMessage = String(stringValue);
            } else {
              // Last resort - show status code
              errorMessage = `Request failed with status ${error.response?.status || 'unknown'}`;
            }
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
      } catch (parseError) {
        console.error('Error parsing error message:', parseError);
        errorMessage = 'An unexpected error occurred';
      }
      
      // Absolutely ensure errorMessage is a string and not [object Object]
      const finalMessage = String(errorMessage);
      if (finalMessage === '[object Object]') {
        console.error('Error message was [object Object], using fallback');
        toast.error('Failed to file document. Check console for details.');
      } else {
        toast.error(finalMessage);
      }
    } finally {
      setIsFilingDocument(false);
    }
  };

  const handleAddTag = async (tagId: number) => {
    await tagsService.addTagToDocument(tagId, document.id);
  };

  const handleRemoveTag = async (tagId: number) => {
    await tagsService.removeTagFromDocument(tagId, document.id);
  };

  const handleReprocess = async () => {
    try {
      setIsProcessing(true);
      // Call the process endpoint
      const response = await api.post(`/documents/${document.id}/process`, {});
      
      toast.success('Document queued for AI processing');
      
      // Wait a bit then reload the document data
      setTimeout(async () => {
        await loadDocumentData();
        setIsProcessing(false);
      }, 3000);
      
    } catch (error: any) {
      console.error('Reprocess error:', error);
      setIsProcessing(false);
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please log in again.');
      } else {
        toast.error(`Failed to reprocess: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  const handleApplyRules = async () => {
    try {
      toast.loading(`Applying AI rules...`, { id: `rules-${document.id}` });
      
      const result = await aiRulesService.applyRulesToDocument(document.id);
      
      if (result.rules_matched > 0) {
        toast.success(`Applied ${result.rules_matched} matching rules`, { id: `rules-${document.id}` });
        // Reload document data to show updates
        await loadDocumentData();
      } else {
        toast(`No rules matched this document`, { id: `rules-${document.id}` });
      }
    } catch (error: any) {
      console.error('Apply rules error:', error);
      toast.error(`Failed to apply rules`, { id: `rules-${document.id}` });
    }
  };

  const handleResetToPending = async () => {
    try {
      toast.loading(`Resetting ${document.filename} to pending...`, { id: `reset-${document.id}` });

      await api.post(`/documents/${document.id}/reset-to-pending`, {});
      
      toast.success(`Document reset to pending`, { id: `reset-${document.id}` });
      
      // Reload the document data to refresh the UI
      await loadDocumentData();
    } catch (error: any) {
      console.error('Reset error:', error);
      toast.error(`Failed to reset document`, { id: `reset-${document.id}` });
    }
  };

  const handleDownload = async () => {
    try {
      // Download the document
      const response = await api.get(`/documents/${document.id}/download`, {
        responseType: 'blob'
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download error:', error);
      console.error('Error response:', error.response);
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please log in again.');
      } else {
        toast.error(`Failed to download file: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  const convertEmailTextToHtml = (text: string): string => {
    // Split into forwarded header and body
    let header = '';
    let body = text;

    const fwdMatch = text.match(/^(-{5,}\s*Forwarded message\s*-{5,}\n(?:From:.*\n)?(?:Date:.*\n)?(?:Subject:.*\n)?(?:To:.*\n)?)/m);
    if (fwdMatch && fwdMatch.index !== undefined) {
      header = fwdMatch[1];
      body = text.substring(fwdMatch.index + fwdMatch[0].length);
    } else {
      const fromMatch = text.match(/^(From:.*\n(?:Date:.*\n)?(?:Subject:.*\n)?(?:To:.*\n)?)/m);
      if (fromMatch && fromMatch.index !== undefined) {
        header = fromMatch[1];
        body = text.substring(fromMatch.index + fromMatch[0].length);
      }
    }

    // Clean body: remove [image: ...] markers and tracking URLs
    body = body
      .replace(/\[image:\s*#\]/gi, '')
      .replace(/\[image:\s*[^\]]*\]/gi, '')
      .replace(/<https?:\/\/t\d+\.em\.[^>]+>/g, '')
      .replace(/<https?:\/\/[^>]{200,}>/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    // Extract key info for bill summary
    const accountMatch = body.match(/Customer Account\s*\n?([\d-]+)/i);
    const addressMatch = body.match(/Service Address\s*\n?([^\n]+)/i);
    const totalMatch = body.match(/\*?Total Due\*?\s*\$?([\d,.]+)/i);
    const newChargesMatch = body.match(/New Charges\s*\$?([\d,.]+)/i);
    const paymentDateMatch = body.match(/(?:Auto )?Payment\s*(?:Scheduled|Due)\s*([A-Za-z]+ \d{1,2},?\s+\d{4})/i);
    const paymentDueMatch = body.match(/Payment due:\s*([A-Za-z]+ \d{1,2},?\s+\d{4})/i);

    // Parse header fields
    const fromField = header.match(/From:\s*(.+)/)?.[1]?.replace(/<[^>]+>/g, '').trim() || '';
    const dateField = header.match(/Date:\s*(.+)/)?.[1]?.trim() || '';
    const subjectField = header.match(/Subject:\s*(.+)/)?.[1]?.trim() || '';

    // Escape HTML in body text
    const escBody = body.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Build clean HTML
    const parts: string[] = [];
    parts.push('<!DOCTYPE html><html><head><style>');
    parts.push('body{font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;margin:0;padding:20px;background:#1a1a2e;color:#e0e0e0}');
    parts.push('.hdr{background:#16213e;border-radius:8px;padding:16px;margin-bottom:16px;border-left:4px solid #0f3460}');
    parts.push('.hdr p{margin:4px 0;font-size:13px}.hdr .l{color:#8899aa}.hdr .v{color:#e0e0e0}');
    parts.push('.card{background:#16213e;border-radius:12px;padding:24px;margin:16px 0;border:1px solid #0f3460}');
    parts.push('.card h3{color:#00d4aa;margin:0 0 16px;font-size:18px}');
    parts.push('.row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #1a2744}');
    parts.push('.row:last-child{border-bottom:none}.rl{color:#8899aa}.rv{color:#e0e0e0;font-weight:600}');
    parts.push('.total{font-size:24px;color:#00d4aa;font-weight:700}');
    parts.push('.pay{background:#0d1b2a;border-radius:8px;padding:12px 16px;margin-top:12px;font-size:13px;color:#8899aa}');
    parts.push('.body{background:#16213e;border-radius:8px;padding:16px;margin-top:16px;white-space:pre-wrap;font-size:13px;line-height:1.6;color:#b0b0b0;max-height:300px;overflow-y:auto}');
    parts.push('summary{cursor:pointer;color:#8899aa;margin-top:16px;font-size:13px}');
    parts.push('</style></head><body>');

    if (fromField) {
      parts.push('<div class="hdr">');
      parts.push('<p><span class="l">From:</span> <span class="v">' + fromField + '</span></p>');
      if (dateField) parts.push('<p><span class="l">Date:</span> <span class="v">' + dateField + '</span></p>');
      if (subjectField) parts.push('<p><span class="l">Subject:</span> <span class="v">' + subjectField + '</span></p>');
      parts.push('</div>');
    }

    if (accountMatch || totalMatch) {
      parts.push('<div class="card"><h3>Bill Summary</h3>');
      if (accountMatch) parts.push('<div class="row"><span class="rl">Account</span><span class="rv">' + accountMatch[1] + '</span></div>');
      if (addressMatch) parts.push('<div class="row"><span class="rl">Service Address</span><span class="rv">' + addressMatch[1].trim() + '</span></div>');
      if (newChargesMatch) parts.push('<div class="row"><span class="rl">New Charges</span><span class="rv">$' + newChargesMatch[1] + '</span></div>');
      if (totalMatch) parts.push('<div class="row"><span class="rl">Total Due</span><span class="total">$' + totalMatch[1] + '</span></div>');
      const pd = paymentDateMatch || paymentDueMatch;
      if (pd) parts.push('<div class="pay">Auto Payment: ' + pd[1] + '</div>');
      parts.push('</div>');
    }

    parts.push('<details><summary>Show full email text</summary><div class="body">' + escBody + '</div></details>');
    parts.push('</body></html>');

    return parts.join('');
  };

  const renderPreview = () => {
    if (document.file_path === 'email_content') {
      // Check if HTML content is available
      const htmlContent = document.extracted_data?.html_content;

      return (
        <div className="p-6 bg-gray-800 rounded-lg">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-white mb-2">Email Content</h3>
            <div className="space-y-2 text-sm text-gray-300">
              <p><span className="text-gray-400">From:</span> {document.from_email}</p>
              <p><span className="text-gray-400">Subject:</span> {document.email_subject}</p>
              <p><span className="text-gray-400">Date:</span> {new Date(document.email_received_at || document.created_at).toLocaleString()}</p>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-4">
            {htmlContent ? (
              <div className="bg-white rounded-lg p-4">
                <iframe
                  srcDoc={htmlContent}
                  className="w-full h-[600px] border-0"
                  sandbox="allow-same-origin"
                  title="Email HTML Content"
                />
              </div>
            ) : (
              <pre className="whitespace-pre-wrap text-gray-300 text-sm">{ocrText}</pre>
            )}
          </div>
        </div>
      );
    }

    // Check if it's an image by mime type or file extension
    const isImage = document.mime_type?.startsWith('image/') || 
                   /\.(jpg|jpeg|png|gif|bmp|tiff?)$/i.test(document.filename);
    
    if (isImage && documentUrl) {
      return (
        <div className="flex justify-center p-4 h-full overflow-auto">
          <img src={documentUrl} alt={document.filename} className="max-w-full h-auto rounded-lg object-contain" />
        </div>
      );
    }

    // Check if it's a PDF by mime type or file extension
    const isPDF = document.mime_type === 'application/pdf' || 
                  document.filename.toLowerCase().endsWith('.pdf');

    if (isPDF) {
      if (documentUrl) {
        return (
          <div className="flex flex-col h-full">
            {/* PDF Viewer with object tag as fallback */}
            <div className="flex-1 overflow-hidden bg-gray-900 relative">
              <object
                data={documentUrl}
                type="application/pdf"
                className="w-full h-full"
                title={document.filename}
              >
                <embed
                  src={documentUrl}
                  type="application/pdf"
                  className="w-full h-full"
                />
                {/* Fallback for browsers that don't support inline PDF */}
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800">
                  <FiFileText className="w-16 h-16 text-gray-400 mb-4" />
                  <p className="text-gray-300 mb-4">Unable to display PDF inline</p>
                  <a
                    href={documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Open PDF in New Tab
                  </a>
                </div>
              </object>
            </div>
            
            {/* Download option */}
            <div className="p-4 bg-gray-800 border-t border-gray-700 text-center">
              <p className="text-sm text-gray-400 mb-2">
                Having trouble viewing? 
              </p>
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => {
                    // Open the blob URL in a new tab
                    if (documentUrl) {
                      window.open(documentUrl, '_blank');
                    }
                  }}
                  className="text-indigo-400 hover:text-indigo-300 underline"
                >
                  Open in new tab
                </button>
                <span className="text-gray-500">or</span>
                <button
                  onClick={handleDownload}
                  className="text-indigo-400 hover:text-indigo-300 underline"
                >
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        );
      } else {
        // Show loading or error state if no URL
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <FiFileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400">Loading PDF...</p>
              <p className="text-sm text-gray-500 mt-2">If this persists, try refreshing the page</p>
            </div>
          </div>
        );
      }
    }

    // Check if this is an email with HTML content (for .txt email files)
    const htmlContent = document.extracted_data?.html_content;
    if (htmlContent && document.from_email) {
      return (
        <div className="p-6 bg-gray-800 rounded-lg h-full overflow-auto">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-white mb-2">Email Content</h3>
            <div className="space-y-2 text-sm text-gray-300">
              <p><span className="text-gray-400">From:</span> {document.from_email}</p>
              <p><span className="text-gray-400">Subject:</span> {document.email_subject}</p>
              <p><span className="text-gray-400">Date:</span> {new Date(document.email_received_at || document.created_at).toLocaleString()}</p>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-4">
            <div className="bg-white rounded-lg p-4">
              <iframe
                srcDoc={htmlContent}
                className="w-full h-[600px] border-0"
                sandbox="allow-same-origin"
                title="Email HTML Content"
              />
            </div>
          </div>
        </div>
      );
    }


    // Check if file content itself is HTML (e.g., .txt email files with HTML body)
    if (fileHtmlContent) {
      return (
        <div className="p-6 bg-gray-800 rounded-lg h-full overflow-auto">
          {document.from_email && (
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white mb-2">Email Content</h3>
              <div className="space-y-2 text-sm text-gray-300">
                <p><span className="text-gray-400">From:</span> {document.from_email}</p>
                <p><span className="text-gray-400">Subject:</span> {document.email_subject}</p>
                <p><span className="text-gray-400">Date:</span> {new Date(document.email_received_at || document.created_at).toLocaleString()}</p>
              </div>
            </div>
          )}
          <div className={document.from_email ? "border-t border-gray-700 pt-4" : ""}>
            <div className="bg-white rounded-lg overflow-hidden">
              <iframe
                srcDoc={fileHtmlContent}
                className="w-full h-[600px] border-0"
                sandbox="allow-same-origin"
                title="Email HTML Content"
              />
            </div>
          </div>
        </div>
      );
    }

    // Check if this is a plain text email (no HTML but has from_email)
    if (document.from_email && !htmlContent) {
      return (
        <div className="p-6 bg-gray-800 rounded-lg h-full overflow-auto">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-white mb-2">Email Content</h3>
            <div className="space-y-2 text-sm text-gray-300">
              <p><span className="text-gray-400">From:</span> {document.from_email}</p>
              <p><span className="text-gray-400">Subject:</span> {document.email_subject}</p>
              <p><span className="text-gray-400">Date:</span> {new Date(document.email_received_at || document.created_at).toLocaleString()}</p>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-4">
            <div className="bg-gray-900 rounded-lg p-4">
              <pre className="text-gray-300 whitespace-pre-wrap font-sans text-sm leading-relaxed">
                {ocrText || 'Email content not available. Check the Extracted Text tab.'}
              </pre>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <FiFileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-400">Preview not available for this file type</p>
          <p className="text-sm text-gray-500 mt-2">{document.mime_type}</p>
        </div>
      </div>
    );
  };

  const content = (
    <>
      {/* Header - only show if not embedded */}
      {!isEmbedded && (
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">Document Review</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>
      )}

        {/* Tabs */}
        <div className="flex border-b border-gray-700">
          {['preview', 'ocr'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`px-6 py-3 text-sm font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'text-indigo-400 border-b-2 border-indigo-400'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              {tab === 'preview' ? 'Preview & Suggestions' : 'Extracted Text'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden relative">
          
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-400">Loading...</div>
            </div>
          ) : (
            <div className="h-full">
              {activeTab === 'preview' && (
                <div className="h-full flex">
                  {/* Document Preview - Left Side */}
                  <div className="flex-1 h-full overflow-hidden border-r border-gray-700 relative">
                    {renderPreview()}
                  </div>
                  
                  {/* Suggestions Panel - Right Side */}
                  <div className="w-96 h-full overflow-y-auto bg-gray-800 p-6" style={{ position: 'relative', zIndex: 1 }}>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-semibold text-white">AI Suggestions</h3>
                      <div className="flex items-center space-x-2">
                        {/* Retry AI Button */}
                        <button
                          type="button"
                          onClick={handleRetryAI}
                          disabled={isProcessing}
                          className="flex items-center gap-1 text-xs px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Retry AI extraction to get better suggestions"
                        >
                          {isProcessing ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                              <span>Processing...</span>
                            </>
                          ) : (
                            <>
                              <FiZap className="w-3 h-3" />
                              <span>Retry AI</span>
                            </>
                          )}
                        </button>
                        
                        {/* Force Complete Button - Show for failed documents */}
                        {document.processing_status === 'failed' && (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const response = await api.post(`/stable-docs/force-complete/${document.id}`);
                                if (response.data.success) {
                                  toast.success('Document marked as completed!');
                                  setTimeout(() => loadDocumentData(), 1000);
                                }
                              } catch (error) {
                                toast.error('Failed to force complete');
                              }
                            }}
                            className="flex items-center gap-1 text-xs px-3 py-1.5 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
                            title="Force mark as completed without processing"
                          >
                            <FiCheckCircle className="w-3 h-3" />
                            <span>Force Complete</span>
                          </button>
                        )}
                        
                        <span className="text-xs text-gray-400">✏️ All fields are editable</span>
                        <button
                          type="button"
                          onClick={() => {
                            // Clear all input fields
                            const inputs = ['expense-filename', 'expense-vendor', 'expense-amount', 'expense-date'];
                            inputs.forEach(id => {
                              const input = window.document.getElementById(id) as HTMLInputElement;
                              if (input) input.value = '';
                            });
                            const textarea = window.document.getElementById('expense-description') as HTMLTextAreaElement;
                            if (textarea) textarea.value = '';
                          }}
                          className="text-xs px-2 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
                        >
                          Clear All
                        </button>
                      </div>
                    </div>
                    
                    
                    {/* Input fields container */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          <FiFileText className="inline-block w-4 h-4 mr-1" />
                          Suggested Filename
                        </label>
                        <input
                          id="expense-filename"
                          name="filename"
                          type="text"
                          defaultValue={suggestions.filename || ''}
                          placeholder="Enter filename"
                          className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          autoComplete="off"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Vendor/Company
                        </label>
                        <input
                          id="expense-vendor"
                          name="vendor"
                          type="text"
                          defaultValue={suggestions.vendor || ''}
                          placeholder="Enter vendor name"
                          className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          autoComplete="off"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          <FiDollarSign className="inline-block w-4 h-4 mr-1" />
                          Amount <span className="text-xs">(Required for expenses)</span>
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">$</span>
                          <input
                            id="expense-amount"
                            name="amount"
                            type="number"
                            step="0.01"
                            defaultValue={suggestions.amount || ''}
                            placeholder="0.00"
                            className="w-full pl-8 pr-3 py-2 bg-gray-700 text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            autoComplete="off"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Leave blank for non-expense documents</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          <FiCalendar className="inline-block w-4 h-4 mr-1" />
                          Date
                        </label>
                        <input
                          id="expense-date"
                          name="date"
                          type="date"
                          defaultValue={suggestions.date || 
                            (document.document_date ? new Date(document.document_date).toISOString().split('T')[0] : '') ||
                            (document.extracted_data?.date || '')}
                          className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          autoComplete="off"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Document Type
                        </label>
                        <select
                          value={selectedDocumentType}
                          onChange={(e) => setSelectedDocumentType(e.target.value)}
                          className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg text-sm"
                        >
                          <option value="">Select type...</option>
                          {documentTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Description
                        </label>
                        <textarea
                          id="expense-description"
                          name="description"
                          defaultValue={suggestions.description || ''}
                          placeholder="Enter expense description"
                          rows={2}
                          className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          autoComplete="off"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          <FiMapPin className="inline-block w-4 h-4 mr-1" />
                          Property (Optional)
                          {document.property_id && document.property_id !== selectedPropertyId && (
                            <span className="ml-2 text-xs text-yellow-400">
                              (Currently in: {properties.find(p => p.id === document.property_id)?.name || 'Unknown'})
                            </span>
                          )}
                        </label>
                        <select
                          value={selectedPropertyId || ''}
                          onChange={(e) => setSelectedPropertyId(e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg text-sm"
                        >
                          <option value="">General/All Properties</option>
                          {properties.map(property => (
                            <option key={property.id} value={property.id}>
                              {property.name || property.address}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-400">
                          Leave as "General" for business-wide expenses
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          Category
                        </label>
                        <select
                          value={selectedCategoryId || ''}
                          onChange={(e) => setSelectedCategoryId(e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg text-sm"
                        >
                          <option value="">Uncategorized</option>
                          {categories.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                        {!selectedPropertyId && (
                          <p className="mt-1 text-xs text-blue-400">
                            💡 Business expense categories available (Vehicle, Office, etc.)
                          </p>
                        )}
                      </div>
                      
                      {/* Tag Selector */}
                      <TagSelector
                        selectedTags={documentTags}
                        onTagsChange={setDocumentTags}
                        onAddTag={handleAddTag}
                        onRemoveTag={handleRemoveTag}
                      />
                      
                      {/* Document Actions Section */}
                      <div className="pt-6 space-y-4">
                        <div className="border-t border-gray-700 pt-4">
                          <h4 className="text-sm font-medium text-gray-400 mb-4">Document Actions</h4>
                        </div>
                        
                        {/* AI Processing Section */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                            <span>AI Processing</span>
                            {document.extracted_data && Object.keys(document.extracted_data).length > 0 && (
                              <span className="flex items-center gap-1">
                                <FiCheckCircle className="w-3 h-3 text-green-400" />
                                Processed
                              </span>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={handleReprocess}
                              disabled={isProcessing}
                              className={`flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                                document.extracted_data && Object.keys(document.extracted_data).length > 0
                                  ? 'bg-gray-600 hover:bg-gray-700 text-white'
                                  : 'bg-purple-600 hover:bg-purple-700 text-white'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {isProcessing ? (
                                <>
                                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                                  <span>Processing</span>
                                </>
                              ) : (
                                <>
                                  {document.extracted_data && Object.keys(document.extracted_data).length > 0 ? (
                                    <FiRefreshCw className="w-3 h-3" />
                                  ) : (
                                    <FiCpu className="w-3 h-3" />
                                  )}
                                  <span>{document.extracted_data && Object.keys(document.extracted_data).length > 0 ? 'Reprocess' : 'Process AI'}</span>
                                </>
                              )}
                            </button>
                            
                            <button
                              onClick={handleApplyRules}
                              className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors"
                            >
                              <FiZap className="w-3 h-3" />
                              <span>Apply Rules</span>
                            </button>
                          </div>
                          
                          {/* Reset to Pending button - show for failed or completed documents */}
                          {(document.processing_status === 'failed' || document.processing_status === 'completed') && (
                            <button
                              onClick={handleResetToPending}
                              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors mt-2"
                              title="Reset document to pending status for reprocessing"
                            >
                              <FiRefreshCw className="w-3 h-3" />
                              <span>Reset to Pending</span>
                            </button>
                          )}
                        </div>
                        
                        {/* Save Changes */}
                        <div className="pt-2 space-y-3">
                          {/* Remove from Queue Checkbox - Only show when opened from queue */}
                          {isFromQueue && (
                            <label className="flex items-center space-x-2 text-sm text-gray-300 px-1">
                              <input
                                type="checkbox"
                                checked={removeFromQueue}
                                onChange={(e) => setRemoveFromQueue(e.target.checked)}
                                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-0"
                              />
                              <span>Remove from queue after saving</span>
                            </label>
                          )}
                          
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleSaveChanges();
                            }}
                            className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center"
                          >
                            <FiSave className="w-4 h-4 mr-2" />
                            Save Changes
                          </button>
                        </div>
                        
                        {/* Filing Options */}
                        <div className="space-y-3 pt-2">
                          <div className="text-xs text-gray-500 mb-2">Filing Options</div>
                          
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log('Create Expense button clicked');
                              handleCreateExpense();
                            }}
                            disabled={isCreatingExpense || isFilingDocument}
                            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={''}
                          >
                            {isCreatingExpense ? (
                              <span className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Creating Expense...
                              </span>
                            ) : (
                              'Create Expense'
                            )}
                          </button>
                          
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log('File Document button clicked');
                              handleFileDocument();
                            }}
                            disabled={isFilingDocument || isCreatingExpense}
                            className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isFilingDocument ? (
                              <span className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Filing Document...
                              </span>
                            ) : (
                              'File Document (No Expense)'
                            )}
                          </button>
                          
                          <p className="text-xs text-gray-400 text-center mt-2">
                            <span className="text-indigo-400">Expense</span>: Invoices, receipts<br/>
                            <span className="text-green-400">File only</span>: Notices, contracts
                          </p>
                        </div>
                        
                        {/* Show Update Property button if document is completed but in wrong property */}
                        {document.processing_status === 'completed' && (
                          <div className="mt-4 pt-4 border-t border-gray-700">
                            <p className="text-xs text-yellow-400 mb-2">
                              Document has an expense but needs to be moved?
                            </p>
                            <button
                              type="button"
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log('Update Property button clicked');
                                
                                // selectedPropertyId can be null (for "All Properties") or a number
                                // Both are valid values
                                
                                if (selectedPropertyId === document.property_id) {
                                  toast.success('Document is already in the selected property');
                                  return;
                                }
                                
                                try {
                                  // Get the suggested filename from the input field
                                  const filenameInput = window.document.getElementById('expense-filename') as HTMLInputElement;
                                  const suggestedFilename = filenameInput?.value || suggestions.filename;
                                  
                                  const updateData: any = {
                                    property_id: selectedPropertyId
                                  };
                                  
                                  // Update filename if it was changed
                                  if (suggestedFilename && suggestedFilename !== document.filename) {
                                    updateData.suggested_copy_destination_name = suggestedFilename;
                                  }
                                  
                                  // Preserve tags by including current tag IDs
                                  if (documentTags && documentTags.length > 0) {
                                    updateData.tag_ids = documentTags.map(tag => tag.id);
                                  }
                                  
                                  // Update document type if selected
                                  if (selectedDocumentType) {
                                    updateData.document_type = documentTypeMap[selectedDocumentType] || 'other';
                                  }
                                  
                                  console.log('Updating document with:', updateData);
                                  await api.patch(`/documents/${document.id}`, updateData);
                                  toast.success('Document updated successfully!');
                                  
                                  // Close modal and refresh
                                  setTimeout(() => {
                                    onClose();
                                  }, 1000);
                                } catch (error: any) {
                                  console.error('Error updating document property:', error);
                                  toast.error('Failed to update document property');
                                }
                              }}
                              className="w-full px-3 py-1.5 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 transition-colors"
                            >
                              Update Document Info
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {activeTab === 'ocr' && (
                <div className="p-6 h-full overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">Extracted Text</h3>
                    {!ocrText && !document.file_path.includes('email_content') && (
                      <button
                        onClick={handleProcessDocument}
                        disabled={isProcessing}
                        className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isProcessing ? 'Processing...' : 'Process with AI'}
                      </button>
                    )}
                  </div>
                  <pre className="whitespace-pre-wrap text-gray-300 text-sm bg-gray-800 p-4 rounded-lg">
                    {ocrText || 'No text extracted yet. Click "Process with AI" to extract text from this document.'}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
    </>
  );

  if (isEmbedded) {
    return (
      <div className="bg-gray-900 rounded-lg w-full h-full flex flex-col">
        {content}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg w-full max-w-7xl h-5/6 flex flex-col">
        {content}
      </div>
    </div>
  );
};